import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { Box } from '~/components/shared/Box';
import { SignUpModalButton } from '~/components/shared/SignUpModalButton';
import { Events } from '~/constants/events';

const { Typography, useColorScheme } = ToucanComponents;

export const FeaturesGrid = () => {
  const theme = useTheme();
  const { t } = useTranslation('subscribe');
  const { textLevel1, textLevel2 } = useColorScheme();
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isViewportAboveMd = useMediaQuery(theme.breakpoints.up('md'));

  const features = [
    {
      key: 'personalizeYourLearning',
      title: 'Personalize your learning',
      description: 'Set goals, choose your language difficulty, and more',
    },
    {
      key: 'saveWordsForLater',
      title: 'Save words for later',
      description: 'Build your vocabulary more quickly by saving words to review and practice later',
    },
    {
      key: 'takeAShortcut',
      title: 'Take a Shortcut',
      description:
        'Put your new language to use more quickly by learning key phrases for common situations like finding the bathroom',
    },
    {
      key: 'enjoyUnlimitedExtraPractice',
      title: 'Enjoy unlimited Extra Practice',
      description: 'Speed up your learning with mini exercises on popular sites like Google and YouTube',
    },
    {
      key: 'pickFromOverDozenLanguages',
      title: 'Pick from over a dozen languages',
      description: 'Toucan has over a dozen languages you can learn including English, Spanish, French and more.',
    },
  ];

  return (
    <Box
      pt={1.5}
      pb={isViewportAboveSm ? 4 : 10}
      css={css`
        background-color: ${ToucanColors.white};
        position: relative;
        &:before {
          position: absolute;
          content: '';
          left: 0;
          top: -39px;
          height: 39px;
          width: 100%;
          background-image: url('/images/new-years-promo/clouds-bg-xs.svg');
          background-position-x: center;
          background-repeat-x: no-repeat;
          background-repeat-y: no-repeat;
          z-index: 5;
          ${theme.breakpoints.up('sm')} {
            background-image: url('/images/new-years-promo/clouds-bg-sm.svg');
            height: 104px;
            top: -104px;
          }
        }
        &:after {
          position: absolute;
          content: '';
          left: 0;
          height: 39px;
          width: 100%;
          background-image: url(/images/new-years-promo/clouds-bg-bottom-xs.svg);
          background-repeat-x: no-repeat;
          background-repeat-y: no-repeat;
          bottom: -39px;
          background-position: center;
          ${theme.breakpoints.up('sm')} {
            background-image: url('/images/new-years-promo/clouds-bg-bottom-sm.svg');
            height: 104px;
            bottom: -104px;
          }
        }
      `}
    >
      <Typography
        variant={isViewportAboveMd ? 'lg' : 'md'}
        font="secondary"
        color={textLevel1}
        css={css`
          text-align: center;
        `}
      >
        What you get with Toucan
      </Typography>
      <Box display="flex" justifyContent="center">
        <Box
          mt={4}
          css={css`
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            row-gap: 40px;
            column-gap: 32px;
            ${theme.breakpoints.up('md')} {
              max-width: 1144px;
            }
          `}
        >
          {features.map(({ key, title, description }) => (
            <Box
              key={title}
              css={css`
                flex: 0 0 calc(16.66% - 40px);
              `}
            >
              <img
                src={`/images/new-years-promo/features-grid/feature-${key}.svg`}
                alt={t('toucanFeaturesThumbnail')}
                css={css`
                  display: block;
                `}
              />
              <Box mt={3}>
                <Typography variant="sm" isBold color={textLevel2}>
                  {title}
                </Typography>
              </Box>
              <Box mt={1}>
                <Typography variant="sm" color={textLevel2}>
                  {description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box mt={7} display="flex" flexDirection="column" alignItems="center" width="100%">
        <SignUpModalButton
          trackingEventName={Events.LearnSmarterPageCTAClicked}
          buttonLocation="Learn Smarter - Features"
        />
      </Box>
    </Box>
  );
};
